import React, { useState } from 'react';
import i18n from '../../utilities/i18n';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import log from '../../utilities/log';
import TextInputWithError from '../ui/forms/TextInputWithError';
import { Button, VStack, Text, HStack } from '@adc/parallax-component-library';
import { Dialog } from 'tamagui';
import { Site } from '../../models';
import { useAppSelector } from '../../reducers/hooks';
import MessageBanner from '../messageBanner/MessageBanner';
import './Modal.scss';
import { siteIdSchema } from 'adc-sirius-module-common';

type CreateSiteModalProps = {
  studyKey: string;
  trigger?: React.ReactNode;
  onSuccess: (site: Site) => Promise<void>;
  site?: Site;
};

/**
 * Modal for creating a new site.
 * @param {string} studyKey - The ID of the study to which the site belongs.
 * @param {React.ReactNode} trigger - The element that triggers the modal.
 * @param {function} onSuccess - Callback function to be called when site creation is successful
 * @param {Site} site - The optional site used for populating initial values
 * @returns {React.JSX.Element}
 */
export default function CreateSiteModal({
  studyKey,
  trigger,
  onSuccess,
  site,
}: CreateSiteModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const organizationKey = useAppSelector(
    (state) => state.organizations.currentOrgKey,
  );

  const initialValues: Partial<Site> = {
    key: site?.key || '',
    name: site?.name || '',
    description: site?.description || '',
    region: site?.region || '',
    type: site?.type || '',
  };

  const formSchema = Yup.object().shape({
    name: Yup.string()
      .required(i18n.t('Form.genericRequired'))
      .matches(
        siteIdSchema.matcher,
        i18n.t('Form.allowedSpecialCharacters', {
          characters: siteIdSchema.allowedSpecialCharacters.join(' '),
        }),
      )
      .test(
        'len',
        () =>
          i18n.t('Form.genericMaxLength', { count: siteIdSchema.maxLength }),
        (val) =>
          siteIdSchema.maxLength && val
            ? val.length < siteIdSchema.maxLength
            : true,
      ),
    description: Yup.string().optional(),
    region: Yup.string().optional(),
    type: Yup.string().optional(),
  });

  const onSubmit = async (values: any, helpers: FormikHelpers<any>) => {
    const requestData = {
      ...values,
      studyKey: studyKey,
      organizationKey,
    };

    return onSuccess(requestData)
      .then(() => {
        helpers.resetForm();
        setIsOpen(false);
        setErrorMessage('');
      })
      .catch((err) => {
        log.error(err);

        if (err.status === 409) {
          setErrorMessage(i18n.t('Modal_CreateSite.error_duplicate'));
        } else {
          setErrorMessage(i18n.t('Modal_CreateSite.error_generic'));
        }
      });
  };

  return (
    <Dialog open={isOpen}>
      <Dialog.Trigger asChild onPress={() => setIsOpen(true)}>
        {trigger}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content">
          <VStack gap="$2">
            <Dialog.Title>
              <Text type="$h3.strong">{i18n.t('Modal_CreateSite.title')}</Text>
            </Dialog.Title>
            {errorMessage ? (
              <MessageBanner
                headerText={i18n.t('Modal_CreateSite.error_header')}
                bodyText={errorMessage}
                type="danger"
                testID="error-banner"
              />
            ) : null}
          </VStack>
          <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            onSubmit={onSubmit}
          >
            {(formikProps: FormikProps<any>) => (
              <Form noValidate>
                <VStack paddingVertical="$4" gap="$2">
                  <TextInputWithError
                    label={i18n.t('Entity.name')}
                    name="name"
                    testID="name"
                  />
                  <TextInputWithError
                    label={i18n.t('Entity.description')}
                    name="description"
                    testID="description"
                  />
                  <TextInputWithError
                    label={i18n.t('Entity.type')}
                    name="type"
                    testID="type"
                  />
                  <TextInputWithError
                    label={i18n.t('Entity.region')}
                    name="region"
                    testID="region"
                  />
                </VStack>
                <HStack justifyContent="flex-end" gap="$2">
                  <Button
                    label={i18n.t('Common.cancel')}
                    variant="secondary"
                    disabled={formikProps.isSubmitting}
                    onPress={() => {
                      setIsOpen(false);
                      setErrorMessage('');
                      formikProps.resetForm();
                    }}
                    testID="cancel-site-creation"
                  />
                  <Button
                    label={i18n.t('Common.save')}
                    disabled={formikProps.isSubmitting || !formikProps.isValid}
                    onPress={formikProps.submitForm}
                    variant="primary"
                    testID="save-site"
                    type="submit"
                  />
                </HStack>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
